<template>
    <div id="our_locations" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <!-- Header -->
        <div class="text-center mb-12" id="locations">
            <h1 class="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-4">
                Stop by one of our 
                <span class="text-orange-600 font-bold">{{ locations.length }}</span> 
                Locations!
            </h1>
            <div class="w-24 h-1 bg-orange-600 mx-auto rounded-full"></div>
        </div>
        
        <!-- Contact Information -->
        <div class="bg-gradient-to-br from-orange-600 to-orange-700 rounded-3xl shadow-xl mb-16">
            <div id="contact" class="grid grid-cols-1 md:grid-cols-3 gap-8 p-8">
                <div
                    v-for="location in locations" 
                    :id="`location_${location.city.toLowerCase()}`"
                    :key="location.city"
                    class="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 text-white transition-transform duration-300 hover:transform hover:scale-105"
                >
                    <div class="flex flex-col items-center text-center space-y-4">
                        <div class="bg-white bg-opacity-20 p-3 rounded-full">
                            <MapPin class="w-6 h-6" />
                        </div>
                        <h3 class="text-2xl font-bold">{{ location.city }}</h3>
                        <address class="not-italic space-y-2">
                            <p>{{ location.street }}</p>
                            <p>{{ location.cityState }}</p>
                            <div class="flex items-center justify-center space-x-2">
                                <Phone class="w-4 h-4" />
                                <p>
                                    <a 
                                        :href="'tel:' + location.phone"
                                        class="hover:text-orange-400"
                                        @click.stop
                                    >
                                        {{ location.phone }}
                                    </a>
                                </p>
                            </div>
                        </address>
                        <button 
                            class="mt-4 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-full text-sm transition-colors"
                            @click.stop="navigateToLocation(location.city)"
                        >
                            View Location
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Images -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div 
                v-for="(image, index) in images" 
                :key="index"
                class="group relative overflow-hidden rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl"
            >
                <OptimizedImage
                    :src="image.url" 
                    :alt="image.alt" 
                    class="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div class="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <p class="text-white text-lg font-semibold">{{ image.alt }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { MapPin, Phone } from 'lucide-vue-next';
import { locations } from '../config/locations';
import OptimizedImage from '@/shared/components/OptimizedImage.vue';
import { useRouter } from 'vue-router';

const router = useRouter();

// Function to navigate to the location detail page
const navigateToLocation = (city: string) => {
  router.push(`/locations/${city.toLowerCase()}`);
};

interface Image {
    url: string;
    alt: string;
}

const images: Image[] = [
    {
        url: '/images/slide-4.jpg',
        alt: 'Orange Muffler Shop'
    },
    {
        url: '/images/slide-6.jpg',
        alt: 'Muffler Shop'
    },
    {
        url: '/images/slide-7.jpg',
        alt: 'Little, Black Van'
    }
];
</script>